import React, { useRef } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { 
  generateSectionReportPDF, 
  generatePDFFromElement 
} from "../../../utils/invoiceUtils";

const ViewIncidentReportModal = ({ 
  isOpen, 
  onClose, 
  campusId,
  teacherId, 
  sectionId, 
  studentId,
  role,
  isSectionReport, 
  sectionName, 
  className,
  campusName
}) => {
  const reportRef = useRef();
  
  // Get data from Redux store
  const { studentIncidentReport: storeIncidentReport, sectionIncidentReports, loading } = useSelector(
    (state) => state.studentIncidentReport
  );

  // Transform API response to match the expected format
  const transformIncidentData = (apiData) => {
    if (!apiData) return null;
    
    // If apiData is already an array, wrap it in the incidents structure
    if (Array.isArray(apiData)) {
      return {
        incidents: apiData.map(incident => ({
          id: incident.id,
          studentId: incident.userId,
          studentName: "Student", // This might need to come from a join or separate call
          dateTime: incident.dateTime,
          location: incident.location,
          incidentType: incident.category,
          severityLevel: incident.severityLevel,
          description: incident.description,
          reportedBy: incident.reporterName,
          actionsTaken: incident.actionTaken,
          status: incident.status,
          involvedParties: incident.involvedParties,
          comments: incident.comments,
          attachment: incident.attachment,
          assignedTo: incident.assignedTo,
          reporterType: incident.reporterType
        }))
      };
    }
    
    return apiData;
  };

  // Use transformed data from store
  const incidentReport = transformIncidentData(storeIncidentReport);
  const sectionReports = isSectionReport && sectionIncidentReports.length > 0 
    ? sectionIncidentReports.map(report => transformIncidentData(report))
    : [];

  // Generate analytics data
  const generateAnalytics = (incidents) => {
    if (!incidents || !Array.isArray(incidents)) return null;

    const totalIncidents = incidents.length;
    const byCategory = incidents.reduce((acc, incident) => {
      acc[incident.incidentType] = (acc[incident.incidentType] || 0) + 1;
      return acc;
    }, {});

    const byLocation = incidents.reduce((acc, incident) => {
      acc[incident.location] = (acc[incident.location] || 0) + 1;
      return acc;
    }, {});

    const bySeverity = incidents.reduce((acc, incident) => {
      acc[incident.severityLevel] = (acc[incident.severityLevel] || 0) + 1;
      return acc;
    }, {});

    const repeatOffenders = incidents.reduce((acc, incident) => {
      const key = `${incident.studentId}-${incident.studentName}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const repeatOffendersList = Object.entries(repeatOffenders)
      .filter(([_, count]) => count > 1)
      .map(([student, count]) => ({ student: student.split('-')[1], count }));

    return {
      totalIncidents,
      byCategory,
      byLocation,
      bySeverity,
      repeatOffenders: repeatOffendersList
    };
  };

  const downloadPDF = () => {
    if (isSectionReport) {
      generateSectionReportPDF(
        sectionReports,
        className,
        sectionName,
        campusName,
        "Section_Incident_Report.pdf"
      );
    } else {
      // Use generatePDFFromElement for individual incident reports
      generatePDFFromElement(reportRef, "Incident_Report.pdf");
    }
  };

  const downloadExcel = () => {
    if (isSectionReport) {
      if (!sectionReports || !sectionReports.length) return;
      
      const workbook = XLSX.utils.book_new();
      const summaryData = [
        ["Incident Reporting Section Report"],
        ["Campus", campusName],
        ["Class", className],
        ["Section", sectionName],
        ["Report Type", role === "admin" ? "Administrative View" : "Teacher View"],
        [],
        ["Incident ID", "Student ID", "Student Name", "Date & Time", "Location", "Type", "Severity", "Description", "Reported By", "Actions Taken", "Status"],
      ];
      
      sectionReports.forEach(report => {
        if (report.incidents && report.incidents.length > 0) {
          report.incidents.forEach(incident => {
            summaryData.push([
              incident.id || "N/A",
              incident.studentId || "N/A",
              incident.studentName || "N/A",
              new Date(incident.dateTime).toLocaleString('en-GB'),
              incident.location || "N/A",
              incident.incidentType || "N/A",
              incident.severityLevel || "N/A",
              incident.description || "N/A",
              incident.reportedBy || "N/A",
              incident.actionsTaken || "N/A",
              incident.status || "N/A"
            ]);
          });
        }
      });
      
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
      
      XLSX.writeFile(workbook, "Section_Incident_Report.xlsx");
    } else {
      if (!incidentReport) return;

      const report = Array.isArray(incidentReport) ? incidentReport[0] : incidentReport;
      if (!report) return;

      const worksheetData = [
        ["Incident Report"],
        ["Campus", campusName],
        ["Report Type", role === "admin" ? "Campus-wide Administrative Report" : "Teacher-specific Report"],
        ["Generated On", new Date().toLocaleDateString('en-GB')],
        [],
        ["Incident ID", "Student ID", "Student Name", "Date & Time", "Location", "Type", "Severity", "Description", "Reported By", "Actions Taken", "Status"],
      ];

      if (report.incidents && Array.isArray(report.incidents)) {
        worksheetData.push(
          ...report.incidents.map((incident) => [
            incident.id || "N/A",
            incident.studentId || "N/A",
            incident.studentName || "N/A",
            new Date(incident.dateTime).toLocaleString('en-GB'),
            incident.location || "N/A",
            incident.incidentType || "N/A",
            incident.severityLevel || "N/A",
            incident.description || "N/A",
            incident.reportedBy || "N/A",
            incident.actionsTaken || "N/A",
            incident.status || "N/A"
          ])
        );
      }

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Incident Report");
      XLSX.writeFile(workbook, "Incident_Report.xlsx");
    }
  };

  if (!isOpen) return null;

  const reportData = !isSectionReport && incidentReport ? (Array.isArray(incidentReport) ? incidentReport[0] : incidentReport) : null;
  const analytics = reportData?.incidents ? generateAnalytics(reportData.incidents) : null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'resolved': 
      case 'closed': return 'text-green-600 bg-green-50';
      case 'in progress': 
      case 'monitoring': return 'text-yellow-600 bg-yellow-50';
      case 'pending': 
      case 'open': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getIncidentTypeColor = (type) => {
    const colors = {
      'bullying': 'bg-red-100 text-red-800',
      'fighting': 'bg-orange-100 text-orange-800',
      'vandalism': 'bg-purple-100 text-purple-800',
      'disruptive behavior': 'bg-yellow-100 text-yellow-800',
      'academic dishonesty': 'bg-blue-100 text-blue-800',
      'inappropriate language': 'bg-pink-100 text-pink-800',
      'tardiness': 'bg-gray-100 text-gray-800',
      'property damage': 'bg-indigo-100 text-indigo-800',
      'incomplete work': 'bg-teal-100 text-teal-800'
    };
    return colors[type?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[95%] max-h-[95vh] overflow-y-auto relative border border-gray-300">
        {loading && <p>Loading incident report...</p>}

        {isSectionReport ? (
          <>
            {sectionReports && sectionReports.length > 0 ? (
              <div>
                {/* Section Report Header */}
                <div className="text-center mb-6 border-b pb-4">
                  <h1 className="text-2xl font-bold text-gray-800">{campusName}</h1>
                  <h2 className="text-xl font-semibold mt-2">Incident Reporting Section Report</h2>
                  <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                    <div><strong>Class:</strong> {className}</div>
                    <div><strong>Section:</strong> {sectionName}</div>
                    <div><strong>Report Type:</strong> {role === "admin" ? "Administrative View" : "Teacher View"}</div>
                    <div><strong>Generated On:</strong> {new Date().toLocaleDateString('en-GB')}</div>
                  </div>
                </div>

                {/* Incidents Summary */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4">Summary</h3>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div className="bg-blue-50 p-4 rounded">
                      <div className="text-2xl font-bold text-blue-600">
                        {sectionReports.reduce((total, report) => total + (report.incidents?.length || 0), 0)}
                      </div>
                      <div className="text-sm text-gray-600">Total Incidents</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded">
                      <div className="text-2xl font-bold text-red-600">
                        {sectionReports.reduce((total, report) => 
                          total + (report.incidents?.filter(i => i.severityLevel === 'High')?.length || 0), 0
                        )}
                      </div>
                      <div className="text-sm text-gray-600">High Severity</div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded">
                      <div className="text-2xl font-bold text-yellow-600">
                        {sectionReports.reduce((total, report) => 
                          total + (report.incidents?.filter(i => ['In Progress', 'Monitoring'].includes(i.status))?.length || 0), 0
                        )}
                      </div>
                      <div className="text-sm text-gray-600">In Progress</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded">
                      <div className="text-2xl font-bold text-green-600">
                        {sectionReports.reduce((total, report) => 
                          total + (report.incidents?.filter(i => ['Resolved', 'Closed'].includes(i.status))?.length || 0), 0
                        )}
                      </div>
                      <div className="text-sm text-gray-600">Resolved</div>
                    </div>
                  </div>
                </div>

                {/* Detailed Incidents List */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Detailed Report</h3>
                  {sectionReports.map((report, reportIndex) => (
                    <div key={reportIndex}>
                      {report.incidents && report.incidents.length > 0 ? (
                        report.incidents.map((incident, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 hover:shadow-md transition-shadow">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                              <div className="lg:col-span-2">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-semibold text-gray-800">
                                    {incident.id} - {incident.studentName}
                                  </h4>
                                  <div className="flex gap-2">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getIncidentTypeColor(incident.incidentType)}`}>
                                      {incident.incidentType || "General"}
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(incident.severityLevel)}`}>
                                      {incident.severityLevel || "Medium"}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-gray-600 text-sm mb-2">
                                  <strong>Location:</strong> {incident.location || "N/A"}
                                </p>
                                <p className="text-gray-700 text-sm">
                                  {incident.description || "No description provided"}
                                </p>
                                <p className="text-gray-600 text-sm mt-2">
                                  <strong>Actions Taken:</strong> {incident.actionsTaken || "N/A"}
                                </p>
                              </div>
                              <div className="space-y-2">
                                <div className="text-sm text-gray-500">
                                  <strong>Student ID:</strong> {incident.studentId || "N/A"}
                                </div>
                                <div className="text-sm text-gray-500">
                                  <strong>Date & Time:</strong> {new Date(incident.dateTime).toLocaleString('en-GB')}
                                </div>
                                <div className="text-sm text-gray-500">
                                  <strong>Reported By:</strong> {incident.reportedBy || "N/A"}
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-500">Status:</span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(incident.status)}`}>
                                    {incident.status || "Pending"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          No incidents found for this section
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No incident reports found for this section</p>
              </div>
            )}
          </>
        ) : reportData ? (
          <>
            {/* Individual Report Header */}
            <div className="text-center mb-6 border-b pb-4">
              <h1 className="text-2xl font-bold text-gray-800">{campusName}</h1>
              <h2 className="text-xl font-semibold mt-2">
                Incident Reporting Report
              </h2>
              <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                <div><strong>Report Type:</strong> {role === "admin" ? "Campus-wide Administrative Report" : "Teacher-specific Report"}</div>
                <div><strong>Generated On:</strong> {new Date().toLocaleDateString('en-GB')}</div>
              </div>
            </div>

            {/* Analytics Dashboard */}
            {analytics && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Trends & Insights</h3>
                
                {/* Total Incidents Summary */}
                <div className="grid grid-cols-4 gap-4 text-center mb-6">
                  <div className="bg-blue-50 p-4 rounded">
                    <div className="text-2xl font-bold text-blue-600">{analytics.totalIncidents}</div>
                    <div className="text-sm text-gray-600">Total Incidents</div>
                  </div>
                  <div className="bg-red-50 p-4 rounded">
                    <div className="text-2xl font-bold text-red-600">
                      {analytics.bySeverity['High'] || 0}
                    </div>
                    <div className="text-sm text-gray-600">High Severity</div>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded">
                    <div className="text-2xl font-bold text-yellow-600">
                      {analytics.bySeverity['Medium'] || 0}
                    </div>
                    <div className="text-sm text-gray-600">Medium Severity</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded">
                    <div className="text-2xl font-bold text-green-600">
                      {analytics.bySeverity['Low'] || 0}
                    </div>
                    <div className="text-sm text-gray-600">Low Severity</div>
                  </div>
                </div>

                {/* Detailed Analytics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  {/* Incidents by Category */}
                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold mb-3">Incidents by Category</h4>
                    {Object.entries(analytics.byCategory).map(([category, count]) => (
                      <div key={category} className="flex justify-between items-center py-1">
                        <span className="text-sm">{category}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>

                  {/* Incidents by Location */}
                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold mb-3">Incidents by Location</h4>
                    {Object.entries(analytics.byLocation).map(([location, count]) => (
                      <div key={location} className="flex justify-between items-center py-1">
                        <span className="text-sm">{location}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>

                  {/* Repeat Offenders */}
                  <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold mb-3">Repeat Offenders</h4>
                    {analytics.repeatOffenders.length > 0 ? (
                      analytics.repeatOffenders.map((offender, index) => (
                        <div key={index} className="flex justify-between items-center py-1">
                          <span className="text-sm">{offender.student}</span>
                          <span className="font-medium text-red-600">{offender.count} incidents</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No repeat offenders</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Detailed Incidents Table */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Detailed Incidents</h3>
              {reportData.incidents && reportData.incidents.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border border-gray-300 px-4 py-2 text-left">Incident ID</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Student ID & Name</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Date & Time</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Location</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Incident Type</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Severity Level</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Description</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Reported By</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Actions Taken</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.incidents.map((incident, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-4 py-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(incident.severityLevel)}`}>
                              {incident.severityLevel || "Medium"}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-4 py-2 max-w-xs">
                            <div className="truncate" title={incident.description}>
                              {incident.description || "No description provided"}
                            </div>
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            {incident.reportedBy || "N/A"}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 max-w-xs">
                            <div className="truncate" title={incident.actionsTaken}>
                              {incident.actionsTaken || "No actions taken"}
                            </div>
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(incident.status)}`}>
                              {incident.status || "Pending"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No incidents found
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No incident report data available</p>
          </div>
        )}

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4 border-t pt-4">
          <button onClick={downloadPDF} className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded text-white transition-colors">
            Download as PDF
          </button>
          <button onClick={downloadExcel} className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded text-white transition-colors">
            Download as Excel
          </button>
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewIncidentReportModal;